"use client";
import { Button } from "../../components/ui/button";
import {
  Heart,
  Users,
  MessageCircle,
  ArrowRight,
  MapPin,
  Calendar,
  Check,
  X,
  Image as ImageIcon,
  ExternalLink,
  Star,
  Gift,
  Music,
  Camera,
  Utensils,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { motion } from "motion/react";
import { memo, useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { useAppDispatch, useAppSelector } from "../../_hooks/hooks";
// import { getWeddingVenueByURL } from "./services/wedding.services";
import dynamic from "next/dynamic";
const GalleryPopup = dynamic<any>(() => import("./elements/GalleryPopup"), {
  ssr: false,
});
const Preloader = dynamic(() =>
  import("../../components/Preloader").then((mod) => mod.Preloader),
);
// import { getAllCMSByURL } from "../home/services/home.services";
import { createLookupObject, formatPhoneForDisplay, cleanPhoneForTel } from "../../_helper/helper";
import { ICMS } from "../home/homeModel";
import { getWeddingVenueByURL } from "./services/wedding.services";
import { OptimizedImage } from "../../components/common/OptimizedImage";

export default function WeddingVenueDetail({
  initialData,
  cmsData,
}: {
  initialData?: any;
  cmsData: any;
}) {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const params = useParams();
  const URL = params?.URL as string;
  const loading = useAppSelector((state) => state.wedding.loading);
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const storeWeddingVenueDetails = useAppSelector(
    (state) => state.wedding.WeddingVenueDetails,
  );
  // const CMSList = useAppSelector((state) => state.home.CMSList);

  const WeddingVenueDetails =
    Object.keys(storeWeddingVenueDetails || {}).length > 0
      ? storeWeddingVenueDetails
      : initialData;

  const fullURL =
    typeof window !== "undefined"
      ? `${window.location.origin}${pathname}`
      : pathname;

  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const handleImageClick = useCallback((index: number) => {
    setCurrentGalleryIndex(index);
    setGalleryOpen(true);
  }, []);
  const handleGalleryClose = useCallback(() => setGalleryOpen(false), []);
  const handleGalleryIndexChange = useCallback((index: number) => {
    setCurrentGalleryIndex(index);
  }, []);

  const WhatsAppNo = SettingList?.filter(
    (setting: any) => setting?.SettingKey === "WhatsAppNo",
  )[0]?.SettingValue;
  const phone = WhatsAppNo ? WhatsAppNo.replace(/\D+/g, "") : "";
  const whatsappLink = `https://wa.me/${phone}?text=${encodeURIComponent(`Hello, I'd like to know more about ${WeddingVenueDetails?.Title} wedding venue.`)}`;

  useEffect(() => {
    if (cmsData && cmsData?.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  // Hydrate store and prevent re-fetch if initialData exists
  useEffect(() => {
    if (
      initialData &&
      Object.keys(storeWeddingVenueDetails || {}).length === 0
    ) {
      import("./weddingSlice").then((mod) => {
        dispatch(mod.setWeddingVenueDetails(initialData));
      });
    } else if (URL) {
      dispatch(getWeddingVenueByURL(URL));
    }
    // dispatch(getAllCMSByURL("weddings-events-detail"));
  }, [URL, initialData]);

  const parseHTML = (html: string) => {
    return { __html: html || "" };
  };

  return (
    <>
      {loading && <Preloader />}
      <div className="min-h-screen bg-white text-md">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-end overflow-hidden">
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url('${WeddingVenueDetails?.BannerImage || WeddingVenueDetails?.MainImage}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80" />
          </div>

          <div className="container mx-auto px-4 py-20 sm:px-6 lg:px-8 pb-10 relative z-10">
            <div className="">
              <div>
                <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full mb-8">
                  <Heart className="h-5 w-5 text-[#C9A962]" />
                  <span className="text-white tracking-wide text-sm">
                    DREAM WEDDING VENUE
                  </span>
                </div>

                <h1 className="text-[#C9A962] text-3xl md:text-4xl lg:text-5xl mb-8 leading-[1.1] tracking-tight font-playfair">
                  {WeddingVenueDetails?.Title}
                </h1>

                <p className="text-white/95 text-2xl md:text-3xl mb-10 max-w-3xl line-clamp-2 font-playfair">
                  {WeddingVenueDetails?.Tagline}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        {WeddingVenueDetails?.ShortDescription && (
          <section className="py-10 md:py-20 bg-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A962]/5 rounded-full blur-3xl" />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  {lookUpCMSData?.[
                    "Wedding Detail  - Section 1 : Description"
                  ] ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          lookUpCMSData?.[
                            "Wedding Detail  - Section 1 : Description"
                          ]?.Template,
                      }}
                    />
                  ) : (
                    <>
                      <h2 className="text-2xl md:text-4xl mb-12 text-[#1A1A1A] text-center">
                        Your Perfect Wedding Destination
                      </h2>
                    </>
                  )}
                  <div
                    className="text-gray-700  leading-relaxed mb-8 text-justify bulletin-section"
                    dangerouslySetInnerHTML={parseHTML(
                      WeddingVenueDetails?.ShortDescription,
                    )}
                  />
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* Package Highlights */}
        {WeddingVenueDetails?.PackageHighlights && (
          <section className="py-10 md:py-20 bg-[#F9F7F4]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              {lookUpCMSData?.[
                "Wedding Detail - Section 2 : Package Highlights"
              ] ? (
                <div
                  className="text-center mb-20"
                  dangerouslySetInnerHTML={{
                    __html:
                      lookUpCMSData?.[
                        "Wedding Detail - Section 2 : Package Highlights"
                      ]?.Template,
                  }}
                />
              ) : (
                <div className="text-center mb-20">
                  <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                    VENUE FEATURES
                  </p>
                  <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                    Package Highlights
                  </h2>
                  <p className="text-gray-600 text-xl max-w-3xl mx-auto">
                    Everything you need for your dream celebration
                  </p>
                </div>
              )}

              <div className="max-w-6xl mx-auto">
                <div
                  className="prose prose-lg max-w-none bg-white p-12 rounded-3xl shadow-xl bulletin-section"
                  dangerouslySetInnerHTML={parseHTML(
                    WeddingVenueDetails?.PackageHighlights,
                  )}
                />
              </div>
            </div>
          </section>
        )}

        {/* Detailed Description */}
        {WeddingVenueDetails?.LongDescription && (
          <section className="py-10 md:py-20 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="order-2 lg:order-1"
                >
                  {/* <h2 className="text-5xl md:text-6xl mb-10 text-[#1A1A1A]">
                    Create Unforgettable Memories
                  </h2> */}
                  <div
                    className="text-gray-700 leading-relaxed space-y-6 bulletin-section"
                    dangerouslySetInnerHTML={parseHTML(
                      WeddingVenueDetails?.LongDescription,
                    )}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="order-1 lg:order-2"
                >
                  <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                    <OptimizedImage
                      src={WeddingVenueDetails?.MainImage}
                      alt={WeddingVenueDetails?.Title}
                      className="w-full h-full"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* Inclusions & Exclusions */}
        {(WeddingVenueDetails?.Inclusion || WeddingVenueDetails?.Exclusion) && (
          <section className="py-10 md:py-20 bg-[#F9F7F4]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              {lookUpCMSData?.[
                "Wedding Detail - Section 3 : What's Included"
              ] ? (
                <div
                  className="text-center mb-20"
                  dangerouslySetInnerHTML={{
                    __html:
                      lookUpCMSData?.[
                        "Wedding Detail - Section 3 : What's Included"
                      ]?.Template,
                  }}
                />
              ) : (
                <div className="text-center mb-20">
                  <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                    What's Included
                  </h2>
                  <p className="text-gray-600 text-xl max-w-3xl mx-auto">
                    Transparent pricing with comprehensive services
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
                {/* Inclusions */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl p-12 shadow-xl"
                >
                  <div className="flex items-center gap-4 mb-10">
                    <div className="p-4 bg-green-100 rounded-2xl">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl text-[#1A1A1A]">Inclusions</h3>
                      <p className="text-gray-600">
                        All essential services covered
                      </p>
                    </div>
                  </div>
                  <div
                    className="prose prose-lg max-w-none inclusion-section"
                    dangerouslySetInnerHTML={parseHTML(
                      WeddingVenueDetails?.Inclusion,
                    )}
                  />
                </motion.div>

                {/* Exclusions */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl p-12 shadow-xl"
                >
                  <div className="flex items-center gap-4 mb-10">
                    <div className="p-4 bg-red-100 rounded-2xl">
                      <X className="h-8 w-8 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-3xl text-[#1A1A1A]">Exclusions</h3>
                      <p className="text-gray-600">
                        Optional services available
                      </p>
                    </div>
                  </div>
                  <div
                    className="prose prose-lg max-w-none exclusion-section"
                    dangerouslySetInnerHTML={parseHTML(
                      WeddingVenueDetails?.Exclusion,
                    )}
                  />
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* Gallery Section */}
        {WeddingVenueDetails?.WeddingInnerimage &&
          WeddingVenueDetails?.WeddingInnerimage?.length > 0 && (
            <section className="py-10 md:py-20 bg-white">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {lookUpCMSData?.["Wedding Detail - Section 4 : Gallery"] ? (
                  <div
                    className="text-center mb-20"
                    dangerouslySetInnerHTML={{
                      __html:
                        lookUpCMSData?.["Wedding Detail - Section 4 : Gallery"]
                          ?.Template,
                    }}
                  />
                ) : (
                  <div className="text-center mb-20">
                    <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
                      VISUAL JOURNEY
                    </p>
                    <h2 className="text-5xl md:text-6xl mb-8 text-[#1A1A1A]">
                      Wedding Venue Gallery
                    </h2>
                    <p className="text-gray-600 text-xl max-w-3xl mx-auto">
                      Glimpses of celebrations at our beautiful
                      WeddingVenueDetails
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {WeddingVenueDetails?.WeddingInnerimage?.map(
                    (image: any, index: number) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="group relative aspect-square overflow-hidden rounded-3xl cursor-pointer shadow-xl"
                        onClick={() => handleImageClick(index)}
                      >
                        <OptimizedImage
                          src={image?.ImageGallery}
                          alt={`${WeddingVenueDetails.Title} - Image ${index + 1}`}
                          className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                          aspectRatio="1/1"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-black/50 rounded-full p-4">
                            <Camera className="h-8 w-8 text-white" />
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </motion.div>
                    ),
                  )}
                </div>
              </div>
              {galleryOpen && (
                <GalleryPopup
                  images={WeddingVenueDetails?.WeddingInnerimage}
                  currentIndex={currentGalleryIndex}
                  isOpen={galleryOpen}
                  onClose={handleGalleryClose}
                  onIndexChange={handleGalleryIndexChange}
                />
              )}
            </section>
          )}

        {/* Final CTA */}
        <section className="py-10 md:py-20 bg-gradient-to-br from-[#1A1A1A] to-gray-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url('${WeddingVenueDetails?.BannerImage}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            {lookUpCMSData?.["Wedding Detail - Section 5 : CTA"] ? (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    lookUpCMSData?.["Wedding Detail - Section 5 : CTA"]
                      ?.Template,
                }}
              />
            ) : (
              <>
                <Heart className="h-20 w-20 text-[#C9A962] mx-auto mb-10" />
                <h2 className="text-white text-5xl md:text-7xl mb-8">
                  Let's Plan Your Dream Wedding
                </h2>
                <p className="text-white/90 text-2xl max-w-3xl mx-auto mb-16">
                  Share your vision with us, and we'll create a celebration as
                  unique as your love story.
                </p>
              </>
            )}
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                size="lg"
                className="bg-[#B42025] hover:bg-[#8F1A1E] text-white px-12 py-7 text-lg group"
                asChild
              >
                <a
                  href={whatsappLink || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="notranslate"
                  translate="no"
                >
                  <MessageCircle className="mr-2 h-6 w-6" />
                  WhatsApp Wedding Team
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button
                size="lg"
                className="bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white hover:text-[#B42025] px-12 py-7 text-lg"
                asChild
              >
                <Link
                  href={`tel:${cleanPhoneForTel(WhatsAppNo)}`}
                  className="notranslate"
                  translate="no"
                >
                  <Calendar className="mr-2 h-6 w-6" />
                  Call Now: {formatPhoneForDisplay(WhatsAppNo)}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
